import { create } from "zustand";

import {
  ClaimSearchBy,
  ClaimSearchHit,
  PartnerClaim,
  PartnerClaimResource,
  PartnerClaimStatus,
  ResendClaimEmailResult,
} from "../api/partner_claims";

interface PartnerClaimsState {
  claims: PartnerClaim[];
  loading: boolean;
  /**
   * Whether a load has finished at least once. Distinct from `!loading`, which
   * is also true before the first request has been made — and an empty list
   * shown then reads as "nobody is stuck", the one wrong answer this screen
   * must never give.
   */
  hasLoaded: boolean;
  error: string | null;
  loadClaims: (params?: {
    status?: PartnerClaimStatus;
    partnerId?: string;
  }) => Promise<void>;
  resendClaimEmail: (claimId: string) => Promise<ResendClaimEmailResult>;
  /** Candidate hits from the last search, or null when not searching. */
  searchHits: ClaimSearchHit[] | null;
  searching: boolean;
  searchClaims: (q: string) => Promise<void>;
  clearSearch: () => void;
  /** Drops a claim from the worklist once it has been attached. */
  removeClaim: (claimId: string) => void;
}

/**
 * Monotonic id for the newest search. Module scope rather than store state: it
 * is bookkeeping about requests, not something any component renders.
 */
let searchSeq = 0;

export const usePartnerClaimsStore = create<PartnerClaimsState>((set) => ({
  claims: [],
  loading: false,
  hasLoaded: false,
  error: null,
  searchHits: null,
  searching: false,

  loadClaims: async (params) => {
    set({ loading: true, error: null });
    try {
      const claims = await PartnerClaimResource.listUnclaimed(params);
      set({ claims, loading: false, hasLoaded: true });
    } catch (err) {
      set({
        error:
          err instanceof Error ? err.message : "Failed to load partner claims",
        loading: false,
        // Deliberately not set: a failed load has established nothing about who
        // is stuck, so the empty-state line stays hidden and the error shows
        // instead.
      });
    }
  },

  /**
   * Propagates rather than swallowing, unlike the loader above: the refusal
   * messages are the whole answer to "why didn't that send", so the component
   * has to be able to put them in front of whoever pressed the button.
   *
   * Updates the row's send history in place on success. Reloading the whole list
   * would be a second round trip to change two fields, and would reorder nothing
   * — a resend does not change where a claim sits in the worklist.
   *
   * Both fields move together. Bumping the count alone would leave the Reminders
   * column saying "3 · last 2d ago" with only two sends behind it — the count and
   * the timestamp disagreeing in the one column support is told to read before
   * deciding whether another email is the right move at all.
   */
  resendClaimEmail: async (claimId) => {
    const result = await PartnerClaimResource.resendClaimEmail(claimId);
    set((state) => ({
      claims: state.claims.map((claim) =>
        claim.claimId === claimId
          ? {
              ...claim,
              reminderCount: result.reminderCount,
              reminderSentAt: result.reminderSentAt,
            }
          : claim
      ),
    }));
    return result;
  },

  /**
   * Searches for a purchase, rather than filtering what is already loaded.
   *
   * The evidence support has — a name on a card, its last four digits — is not
   * in the worklist at all; it lives in Stripe. So this asks the server, and the
   * answer can include claims the worklist never showed.
   */
  searchClaims: async (q) => {
    // Only the newest search may write. Typing narrows a term faster than Stripe
    // answers, and without this a slower earlier request can land last and show
    // hits for a term no longer in the box — or resurrect results after the box
    // was cleared entirely.
    const seq = ++searchSeq;
    set({ searching: true, error: null });
    try {
      const result = await PartnerClaimResource.search(q);
      if (seq !== searchSeq) return;
      set({ searchHits: result.hits, searching: false });
    } catch (err) {
      if (seq !== searchSeq) return;
      set({
        error: err instanceof Error ? err.message : "Claim search failed",
        searching: false,
        // Left null rather than empty: "the search failed" and "nothing matched"
        // send support to opposite next steps, so they must not render alike.
        searchHits: null,
      });
    }
  },

  /**
   * Abandons any search in flight as well as its results.
   *
   * Deliberately does not clear `error`: a failed *load* is a different problem
   * from a failed search, and wiping it here would leave an empty worklist with
   * no explanation for why.
   */
  clearSearch: () => {
    searchSeq++;
    set({ searchHits: null, searching: false });
  },

  /**
   * An attached claim is no longer unclaimed, so it leaves both views. The
   * server has already dropped it from the index; this keeps the screen honest
   * without a reload.
   */
  removeClaim: (claimId) =>
    set((state) => ({
      claims: state.claims.filter((claim) => claim.claimId !== claimId),
      searchHits:
        state.searchHits?.filter((hit) => hit.claim.claimId !== claimId) ?? null,
    })),
}));
